import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Divider, Select, Upload, Form, Input, message } from "antd";
import FormItem from "components/fields/FormItem";
import { Upload as UploadIcon } from "lucide-react";
import { emailField, requiredField } from "utils/crudSagaHelper";
import { useNavigate } from 'react-router-dom';
import APIService from '../../../../services/apiservice';
import { showLoading, hideLoading } from '../../../../redux/layout/layoutState';
import { 
    doEmployeeReferralCreate,
    getReferralRelationList
 } from '../../../../redux/employeereferral/employeeReferralState';
import SelectWithOthers from "components/fields/SelectWithOthers";

const ReferralForm = ({key, jobPostId}) => {
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const { TextArea } = Input;
  const navigate = useNavigate();
  const [resumeFile, setResumeFile] = useState();

  const recordData = useSelector((state) => state.myProfile?.record?.data);
  const user = useSelector((state) => state.auth.user);
  const referralRelations = useSelector((state) => state.employeeReferral?.referralRelations?.items);

  const referralError = useSelector((state) => state.employeeReferral?.employeeReferralDetails.error);

  useEffect(() => {
        dispatch(getReferralRelationList());
  }, [dispatch]);
    
  useEffect(() => {
    if(recordData){			
      const { contact_details, address, applicant, ...restData } = recordData;
			const initialValues = {
				...Object.fromEntries(
					Object.entries(contact_details || {}).map(([key, value]) => [`contact_${key}`, value])
				),
				...Object.fromEntries(
					Object.entries(address || {}).map(([key, value]) => [`address_${key}`, value])
				),				
        ...Object.fromEntries(
					Object.entries(applicant || {}).map(([key, value]) => [`applicant_${key}`, value])
				),	
				...restData,
			};

			form.resetFields();
			form.setFieldsValue(initialValues);

      }
  }, [recordData, form]);

  const handleUpload = async (file) => {
		const isValidType =
      file.type === 'application/pdf' ||
      file.type === 'application/msword' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    if (!isValidType) {
      message.error('Only PDF, DOC, DOCX files are allowed');
      return Upload.LIST_IGNORE;
    }

    const isLessThan5MB = file.size / 1024 / 1024 < 5;

    if (!isLessThan5MB) {
      message.error('File must be smaller than 5MB');
      return Upload.LIST_IGNORE;
    }

    setResumeFile(file);

    // dispatch(showLoading('Reading...'));
    // const parsedData = await APIService.doUploadRequest(`api/v1/employee-referral/resume/parse`, file);
    
    // // dispatch(getMyProfileSuccess(parsedData));
    // dispatch(hideLoading());

    return false; 
	}

  const onSubmit = async (formData) => {
    try {

      const { resume, ...rest } = formData;
      let attachmentData = null;
      if(resumeFile){
        dispatch(showLoading('Uploading...'));
        const response = await APIService.doUploadRequest(`api/v1/console/employee-referral/resume/upload`, resumeFile);
        attachmentData = response.data;
        dispatch(hideLoading());
      }

      const payload = {
        ...rest,
        ref_resume_id: attachmentData?.att_id || null,
        ref_job_post_id: jobPostId,
        ref_type_id: 46,
        ref_status_id: 1,
        ref_referred_by: user.id,
        ref_referrer_name: user.display_full_name,
      };

      dispatch(doEmployeeReferralCreate(payload));

    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  return (
    <Card>
      
      <div id="referral-form" className="p-5 pt-2 space-y-4">
          <h2 className="text-gray-800 mb-4 text-xl font-medium">Employee Referral Form</h2>
          <Form form={form}
            layout="vertical"
            onFinish={onSubmit}
            > 
            
            <FormItem
              name="resume"
              icon={UploadIcon}
              label="Upload Resume"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) return e;
                return e?.fileList;
              }}
              rules={[
                {
                  required: true,
                  message: 'Please upload resume',
                },
              ]}
            >
              <Upload.Dragger
                accept=".pdf,.doc,.docx"
                maxCount={1}
                beforeUpload={handleUpload}
              >
                <div className="flex flex-col items-left gap-2">
                  <div className="btn-gradient-emerald px-3 py-1.5 rounded-lg w-[100px]">
                      Choose File
                  </div>
                  
                  <p className="text-sm text-left">
                    Accepted formats: PDF, DOC, DOCX (Max 5MB)
                  </p>
                </div>
              </Upload.Dragger>
            </FormItem>
            

            <Divider/>

            <h4 className="text-gray-900 text-base font-medium mb-4">Personal Information</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              <FormItem
                label="First Name"
                name="ref_first_name"
                rules={[requiredField()]}
              >
                  <Input id="ref_first_name"
                    className="input-field"
                    maxLength={100}
                    placeholder="Enter First Name"
                  />
              </FormItem>
              <FormItem
                label="Last Name"
                name="ref_last_name"
                rules={[requiredField()]}
              >
                  <Input id="ref_last_name"
                    className="input-field"
                    maxLength={100}
                    placeholder="Enter Last Name"
                  />
              </FormItem>
              <FormItem
                label="Email Address"
                name="ref_email"
                rules={[requiredField(), emailField()]}
                iconName="Mail"
              >
                  <Input id="ref_email"
                    className="input-field"
                    maxLength={100}
                    placeholder="username@domain.com"
                  />
              </FormItem>
              <FormItem
                label="Phone Number"
                name="ref_phone"
                rules={[requiredField()]}
                iconName="Phone"
              >
                  <Input id="ref_phone"
                    className="input-field"
                    maxLength={20}
                    placeholder="+63 (555) 123-4567"
                  />
              </FormItem> 
              <FormItem
                label="Relation to the candidate"
                name="ref_relation"
                rules={[requiredField()]}
              >
                <SelectWithOthers 
                  mode="edit"
                  selectFieldName="ref_relation" 
                  selectOptions={referralRelations} 
                  othersFieldName="ref_relation_other_specific"
                  optionOthersId={[55]}
		            />
              </FormItem> 
              <div className="col-span-1 md:col-span-2">
              <FormItem
                label="A brief note on why you think they're a great fit"
                name="ref_remarks"
              >
                <TextArea
                  id="ref_remarks"
                  className="input-field"
                  maxLength={100}
                  placeholder="Enter notes on why you think they're a great fit"
                />
              </FormItem> 
              </div>
            </div>
            {referralError && 
              <div className="flex w-full text-red-400">
                {referralError.error}
              </div>
              }
            <div className="flex justify-end mt-4">
              <button className="icon-effect gradient-primary px-5 py-2 font-semibold">
                  Submit Referral
              </button>
            </div>
          </Form>
      </div>
    </Card>
  );
};

export default ReferralForm;